import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { v4 } from 'uuid';
import { buildBounds, getLatLngsForGeojson } from '../../__legacy__/helpers/geometry/polygon.helpers';
import { ACDLError } from '../../__legacy__/models/error.model';
import { IAnnotationService } from '../../__legacy__/modules/annotation/annotation.service';
import { IAnnotationApiService } from '../../__legacy__/modules/annotation/annotationApi.service';
import { IImage, IQuestionModelDto, ISegmentationDto } from '../../__legacy__/modules/annotation/annotations.interface';
import { IAnnotationsStore } from '../../__legacy__/modules/annotation/annotations.store';
import { IAnnotationUiStore } from '../../__legacy__/modules/annotation/annotationUi.store';
import { BaseAnnotationControl } from '../../__legacy__/modules/annotation/baseAnnotationControl';
import { cleanClone, cleanClones, IQuestionModel } from '../../__legacy__/modules/annotation/question.model';
import { IAnnotationTypeBl } from '../../__legacy__/modules/annotation/submodules/annotationTypes/annotationType.bl';
import { IImageAnnotationsResponse } from '../../__legacy__/modules/annotation/submodules/segmentations/segmentationContextMenu/segmentationsApi.service';
import { IDatasetDetails, IDatasetPreviewImage } from '../../__legacy__/modules/datasets/datasetsDetails.store';
import { NotificationLevel, ToastNotification } from '../../__legacy__/modules/notifications/models/notification.model';
import { INotificationsService } from '../../__legacy__/modules/notifications/services/notifications.service';
import { IMainApiService } from '../../__legacy__/services/api.service';
import { IApiService } from '../../__legacy__/services/api.service.base';
import { IApiServiceTraining } from '../../__legacy__/services/api.service.training';
import { IProjectHubService } from '../../__legacy__/services/projectHub.service';
import { browserHistory } from '../../usehistory';
import { ICurrentWorkspaceStore } from '../workspaces/currentWorkspace/CurrentWorkspace.store';
import { IJobDetailsService } from './jobDetails.service';
import { IValidJobType, JOB_RESOURCE_TYPE, JOB_STATUS } from './jobs.model';
import { IPredictionInspectorStore } from './predictionInspector.store';
import { IImagesApiService } from './services/imagesApi.service';
import { IJobDetailsApiService } from './services/jobDetailsApi.service';
import { assertNotNull, buildPredictionImage, getJobRoutePrefix } from './utils';

export interface IPredictionImagePreviewService extends BaseAnnotationControl {
  predictionInspectorStore: IPredictionInspectorStore;
  initializeAsync: (workspaceId: string, jobId: string, jobType: IValidJobType) => Promise<void>;
  updateImageId: (imageId: string) => Promise<void>;
}

export namespace IPredictionImagePreviewService {
  export const $: interfaces.ServiceIdentifier<IPredictionImagePreviewService> = Symbol('PREDICTION_IMAGES_PREVIEW_SERVICE');
}

@injectable()
export class JobPredictionImagesService extends BaseAnnotationControl implements IPredictionImagePreviewService {
  PREFETCH_LIMIT = 2;
  predictionInspectorStore: IPredictionInspectorStore;

  constructor(
    @inject(IPredictionInspectorStore.$) predictionInspectorStore: IPredictionInspectorStore,
    @inject(INotificationsService.$) notificationsService: INotificationsService,
    @inject(IAnnotationUiStore.$) uiStore: IAnnotationUiStore,
    @inject(IAnnotationsStore.$) annotationsStore: IAnnotationsStore,
    @inject(IJobDetailsService.$) private readonly jobDetailsService: IJobDetailsService,
    @inject(IAnnotationTypeBl.$) private readonly annotationTypeBl: IAnnotationTypeBl,
    @inject(IAnnotationApiService.$) private readonly annotationApiService: IAnnotationApiService,
    @inject(IAnnotationService.$) private readonly annotationService: IAnnotationService,
    @inject(IMainApiService.$) private readonly apiService: IApiService,
    @inject(IProjectHubService.$) private readonly projectHubService: IProjectHubService,
    @inject(IApiServiceTraining.$) private readonly apiTrainingService: IApiService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspace: ICurrentWorkspaceStore,
    @inject(IJobDetailsApiService.$) private readonly jobDetailsApiService: IJobDetailsApiService,
    @inject(IImagesApiService.$) private readonly imagesApiService: IImagesApiService
  ) {
    super(uiStore, notificationsService, annotationsStore);
    makeObservable(this, {
      showImage: action,
      cleanupAnnotations: action,
      parseAnnotations: action,
      requestQuestionsAsync: action,
    });
    this.predictionInspectorStore = predictionInspectorStore;
  }

  setupAsync = async (): Promise<void> => {
    return Promise.resolve();
  };

  handleImageDisplayed = (_image: IImage): void => {};

  async updateImageId(imageId: string): Promise<void> {
    this.uiStore.changeIsImageLoading(true);
    this.cleanupAnnotations();
    this.predictionInspectorStore.imageId = imageId;
    this.annotationsStore.setImageName('', '', 'None');
    this.annotationsStore.image = undefined;
    this.refreshQuestionsIfNeeded();
    await this.showImage();
    // Try to load 5 images from each side
    const currentImageIndex = this.predictionInspectorStore.indexOf;
    if (currentImageIndex === -1 || !this.predictionInspectorStore.images) {
      return;
    }

    for (let i = 0; i < this.PREFETCH_LIMIT; i++) {
      const nextImageIndex = currentImageIndex + i;
      const previousImageIndex = currentImageIndex - i;

      if (nextImageIndex < this.predictionInspectorStore.images.length) {
        const nextImage = this.predictionInspectorStore.images[nextImageIndex];
        void this.ensureCached(nextImage.datasetId, nextImage.id);
      }

      if (previousImageIndex >= 0) {
        const previousImage = this.predictionInspectorStore.images[previousImageIndex];
        void this.ensureCached(previousImage.datasetId, previousImage.id);
      }
    }
  }

  getResourceKeys(imageId: string): { heatmapKey: string; maskKey: string } {
    return {
      heatmapKey: `HEATMAP-${imageId}`,
      maskKey: `MASK-${imageId}`,
    };
  }

  async ensureCached(datasetId: string, imageId: string): Promise<void> {
    const imageKey = `${datasetId}-${imageId}`;
    const imagePromises = [];
    const { datasetsCache, imageDetailsCache, annotationsCache, metricsCache, imagesObjectUrl } = this.predictionInspectorStore;
    const { job, jobType } = this.jobDetailsService.store;
    const { currentWorkspace } = this.currentWorkspace;

    if (!datasetsCache.has(datasetId)) {
      datasetsCache.set(datasetId, this.imagesApiService.getDatasetDetailsAsync(datasetId));
    }

    if (!imageDetailsCache.has(imageKey)) {
      imageDetailsCache.set(imageKey, this.imagesApiService.getImageDetailsAsync(datasetId, imageId));
    }

    if (!annotationsCache.has(imageId)) {
      annotationsCache.set(imageId, this.imagesApiService.getImageAnnotationsAsync(imageId));
    }

    // Metrics cache should be initialized at this point
    const cachedMetrics = metricsCache.get(imageId);
    const { maskKey, heatmapKey } = this.getResourceKeys(imageId);
    if (cachedMetrics && job.id !== null) {
      if (cachedMetrics.mask_id && !imagesObjectUrl.has(maskKey)) {
        imagePromises.push(
          this.apiTrainingService
            .getImageAsync(this.jobDetailsApiService.getResourceUrl(currentWorkspace.id, job.id, jobType, cachedMetrics.mask_id, JOB_RESOURCE_TYPE.MASK))
            .then((image) => {
              if (image instanceof ACDLError) {
                return;
              }

              imagesObjectUrl.set(maskKey, URL.createObjectURL(image.blob));
            })
        );
      }

      if (cachedMetrics.heatmap_id && !imagesObjectUrl.has(heatmapKey)) {
        imagePromises.push(
          this.apiTrainingService
            .getImageAsync(this.jobDetailsApiService.getResourceUrl(currentWorkspace.id, job.id, jobType, cachedMetrics.heatmap_id, JOB_RESOURCE_TYPE.HEATMAP))
            .then((image) => {
              if (image instanceof ACDLError) {
                return;
              }

              imagesObjectUrl.set(heatmapKey, URL.createObjectURL(image.blob));
            })
        );
      }
    }

    if (!imagesObjectUrl.has(imageId)) {
      imagePromises.push(
        this.apiService.getImageAsync(this.apiService.getUrl(`/Images/GetImage/${imageId}`)).then((image) => {
          if (image instanceof ACDLError) {
            return;
          }

          imagesObjectUrl.set(imageId, URL.createObjectURL(image.blob));
        })
      );
    }
    await Promise.allSettled(imagePromises);
  }

  async getImageMetrics(): Promise<void> {
    const { image, imagesObjectUrl } = this.predictionInspectorStore;
    const { job, jobType, annotationTypes } = this.jobDetailsService.store;

    assertNotNull(image, 'Tried to get image metrics without image selected');
    assertNotNull(job.id, 'Treid to get image metrics without job');
    const cachedPrediction = this.predictionInspectorStore.metricsCache.get(image.id);
    if (!cachedPrediction) {
      return;
    }

    const { currentWorkspace } = this.currentWorkspace;
    const { heatmap_id, mask_id } = cachedPrediction;
    const predictionDetails = buildPredictionImage(
      image,
      job,
      cachedPrediction,
      heatmap_id ? this.jobDetailsApiService.getResourceUrl(currentWorkspace.id, job.id, jobType, heatmap_id, JOB_RESOURCE_TYPE.HEATMAP) : null,
      mask_id ? this.jobDetailsApiService.getResourceUrl(currentWorkspace.id, job.id, jobType, mask_id, JOB_RESOURCE_TYPE.MASK) : null,
      annotationTypes
    );
    if (predictionDetails.heatmapUrl) {
      const { heatmapKey } = this.getResourceKeys(image.id);
      const cachedObjectURL = imagesObjectUrl.get(heatmapKey);
      if (cachedObjectURL) {
        predictionDetails.heatmapUrl = cachedObjectURL;
      } else {
        const heatmapFullImage = await this.apiTrainingService.getImageAsync(predictionDetails.heatmapUrl);

        if (!(heatmapFullImage instanceof ACDLError)) {
          const objectURL = URL.createObjectURL(heatmapFullImage.blob);
          predictionDetails.heatmapUrl = objectURL;
          imagesObjectUrl.set(heatmapKey, objectURL);
        }
      }
    }

    if (predictionDetails.maskUrl) {
      const { maskKey } = this.getResourceKeys(image.id);
      const cachedObjectURL = imagesObjectUrl.get(maskKey);
      if (cachedObjectURL) {
        predictionDetails.maskUrl = cachedObjectURL;
      } else {
        const maskFullImage = await this.apiTrainingService.getImageAsync(predictionDetails.maskUrl);

        if (!(maskFullImage instanceof ACDLError)) {
          const objectURL = URL.createObjectURL(maskFullImage.blob);
          predictionDetails.maskUrl = objectURL;
          imagesObjectUrl.set(maskKey, objectURL);
        }
      }
    }

    this.predictionInspectorStore.predictionDetails = predictionDetails;
  }

  async getCachedValue<V>(key: string, store: Map<string, V | Promise<V | ACDLError>>, getter: () => Promise<V | ACDLError>): Promise<V | null> {
    const cachedValue = store.get(key);
    if (cachedValue) {
      const resolvedValue = await cachedValue;

      if (resolvedValue instanceof ACDLError) {
        store.delete(key);
      } else {
        return resolvedValue;
      }
    }
    const valueToCache = await getter();

    if (valueToCache instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'something_went_wrong'));
      return null;
    }

    store.set(key, valueToCache);
    return valueToCache;
  }

  async getImageInfo(datasetId: string, imageId: string): Promise<IDatasetPreviewImage | null> {
    const imageKey = `${datasetId}-${imageId}`;
    return await this.getCachedValue(
      imageKey,
      this.predictionInspectorStore.imageDetailsCache,
      async () => await this.imagesApiService.getImageDetailsAsync(datasetId, imageId)
    );
  }

  async getDatasetInfo(datasetId: string): Promise<IDatasetDetails | null> {
    return await this.getCachedValue(
      datasetId,
      this.predictionInspectorStore.datasetsCache,
      async () => await this.imagesApiService.getDatasetDetailsAsync(datasetId)
    );
  }

  async getAnnotationsInfo(imageId: string): Promise<IImageAnnotationsResponse | null> {
    return await this.getCachedValue(
      imageId,
      this.predictionInspectorStore.annotationsCache,
      async () => await this.imagesApiService.getImageAnnotationsAsync(imageId)
    );
  }

  async initializeStore(workspaceId: string, jobId: string, jobType: IValidJobType): Promise<void> {
    const { job: jobDetails } = this.jobDetailsService.store;
    const { jobId: loadedJobId } = this.predictionInspectorStore;
    // We have predictions loaded for some job, but it isn't requested one
    if (jobId === loadedJobId) {
      return;
    }

    this.predictionInspectorStore.clearCache();
    this.predictionInspectorStore.clearPredictionDetails();

    // We have some job loaded in the store, but it isn't requested one
    if (jobDetails.id !== jobId) {
      this.jobDetailsService.store.resetStore(false, jobType);
      await this.jobDetailsService.populateJobDetailsAsync(jobId);
    }
    const datasetIds = this.jobDetailsService.store.job.datasetIds;
    // Fetch images for each dataset
    const images = await this.imagesApiService.getDatasetsImagesAsync(datasetIds);

    if (images instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'something_went_wrong'));
      return;
    }

    this.predictionInspectorStore.images = images;

    if (!this.predictionInspectorStore.images) {
      return;
    }

    const metrics = await this.jobDetailsApiService.getJobImagePredictionsAsync(
      workspaceId,
      jobId,
      this.predictionInspectorStore.images.map((image) => image.id),
      this.jobDetailsService.store.jobType
    );

    if (metrics instanceof ACDLError) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'something_went_wrong'));
      return;
    }

    this.predictionInspectorStore.metricsCache = new Map(Object.entries(metrics));
  }

  async initializeAsync(workspaceId: string, jobId: string, jobType: IValidJobType): Promise<void> {
    // Initialize when job changed
    await this.initializeStore(workspaceId, jobId, jobType);
    const { job } = this.jobDetailsService.store;
    assertNotNull(job.id, 'Tried to continue ininitalization without job');

    if (job.status !== JOB_STATUS.FINISHED && job.status !== JOB_STATUS.IMPORTED) {
      browserHistory.replace(getJobRoutePrefix(jobType).List.All.Path);
    }

    const imagesProjectId = job.projectId;
    const projectId = job.projectId;
    if (projectId) {
      this.annotationsStore.projectId = projectId;
      await this.annotationTypeBl.freeAccessStarted(projectId, false);
      if (this.predictionInspectorStore.images && imagesProjectId !== null) {
        const imagesAdditionalInfo = await this.imagesApiService.getProjectImagesAsync({
          orderBy: 'name',
          orderType: 'asc',
          pageNumber: 1,
          pageSize: this.predictionInspectorStore.images.length,
          projectId: imagesProjectId,
          datasetsIds: job.datasetIds ?? [],
        });

        if (imagesAdditionalInfo instanceof ACDLError) {
          await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'something_went_wrong'));
          return;
        }

        this.predictionInspectorStore.images = this.predictionInspectorStore.images.map((image, index) => ({
          ...image,
          imageSetType: imagesAdditionalInfo.data[index].imageSet,
        }));
      }
    } else {
      this.annotationsStore.projectId = '';
    }
    await this.requestQuestionsAsync();
    await this.projectHubService.initializeAsync();
  }

  async showImage(): Promise<void> {
    const image = this.predictionInspectorStore.image;
    assertNotNull(image, 'Tried to show image without one being selected');
    const imageInfoPromises: [Promise<IDatasetPreviewImage | null>, Promise<IDatasetDetails | null>] = [
      this.getImageInfo(image.datasetId, image.id),
      this.getDatasetInfo(image.datasetId),
    ];

    const imageInfos: [IDatasetPreviewImage | null, IDatasetDetails | null] = await Promise.all(imageInfoPromises);
    const imageInfo = imageInfos[0];
    const datasetInfo = imageInfos[1];

    if (imageInfo === null || datasetInfo === null) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'something_went_wrong'));
      return;
    }

    const savedObjectUrl = this.predictionInspectorStore.imagesObjectUrl.get(image.id);
    let url: string | undefined = savedObjectUrl;
    if (!url) {
      const imageFull = await this.apiService.getImageAsync(this.apiService.getUrl(`/Images/GetImage/${image.id}`));

      if (imageFull instanceof ACDLError) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, 'something_went_wrong'));
        return;
      }
      const objectUrl = URL.createObjectURL(imageFull.blob);
      url = objectUrl;
      this.predictionInspectorStore.imagesObjectUrl.set(image.id, objectUrl);
    }

    // Get annotations
    const annotationsData = await this.getAnnotationsInfo(imageInfo.id);
    if (!annotationsData) {
      return;
    }

    const annotations = annotationsData.annotations.find((annotation) => annotation.projectId === this.annotationsStore.projectId);

    const annotationImage: IImage = {
      annotations,
      lowQualityUrl: '',
      id: imageInfo.id,
      name: imageInfo.name,
      url,
      height: imageInfo.height,
      width: imageInfo.width,
      size: imageInfo.size,
      dataSetName: datasetInfo.name,
      datasetStatus: datasetInfo.status,
      lockedByName: '',
    };

    this.annotationsStore.setImageName(annotationImage.name, annotationImage.dataSetName, annotationImage.datasetStatus);
    await this.getImageMetrics();
    this.annotationsStore.image = annotationImage;
    this.parseAnnotations(annotationImage);
  }

  cleanupAnnotations(): void {
    this.annotationsStore.id = undefined;
    this.annotationsStore.setSegmentations([]);
    this.annotationsStore.questions.replace(cleanClones(this.annotationsStore.questions));
  }

  parseAnnotations(image: IImage): void {
    const annotationInfoResult = image.annotations;

    if (!annotationInfoResult) {
      this.cleanupAnnotations();
      return;
    }

    const annotations = annotationInfoResult.annotations;
    if (annotations === undefined) {
      throw new ACDLError('No annotations found', undefined);
    }
    this.annotationsStore.id = annotationInfoResult.id;
    const segmentations = annotations.segmentations.map((s: ISegmentationDto) => {
      const attributes = this.mapQuestions(s.feature.properties?.annotationTypeId, s.questions);
      const feature = {
        ...s.feature,
        id: v4(),
        color: this.annotationService.getAnnotationTypeColor(s.feature.properties?.annotationTypeId),
        featureType: s.feature.properties?.featureType,
      };

      const latlngs = getLatLngsForGeojson(feature);

      return {
        feature,
        latlngs,
        id: s.id,
        questions: attributes,
        bbox: buildBounds(latlngs),
        priority: s.priority,
      };
    });

    this.annotationsStore.setSegmentations(segmentations.filter((s) => s.feature.color !== ''));
    this.annotationsStore.questions.replace(this.mapQuestions(undefined, annotationInfoResult.annotations.questions));
    this.annotationService.deselectSegmentation();
  }

  mapQuestions(annotationTypeId: string | undefined, questionsDtos: IQuestionModelDto[]): IQuestionModel[] {
    const currentQuestions = annotationTypeId
      ? this.annotationsStore.questions.filter((q) => q.scopes.includes(annotationTypeId))
      : this.annotationsStore.questions;

    const models: IQuestionModel[] = [];
    for (const currentQuestion of currentQuestions) {
      const existingAnswer = questionsDtos.find((x) => x.id === currentQuestion.id);
      models.push(cleanClone(currentQuestion, existingAnswer));
    }

    return models;
  }

  refreshQuestionsIfNeeded(): void {
    if (this.annotationsStore.projectId.length === 0) {
      return;
    }

    if (this.projectHubService.didQuestionsChangedFor(this.annotationsStore.projectId)) {
      const result = this.projectHubService.popNewQuestionsFor(this.annotationsStore.projectId);
      this.predictionInspectorStore.annotationsCache.clear();
      this.annotationsStore.questions.replace(cleanClones(result));
      this.annotationService.deselectSegmentation();
    }
  }

  async requestQuestionsAsync(): Promise<void> {
    if (this.annotationsStore.projectId.length === 0) {
      return;
    }

    const result = await this.annotationApiService.requestQuestionsAsync(this.annotationsStore.projectId);
    if (result instanceof Error) {
      throw result;
    }
    this.annotationsStore.questions.replace([...result]);
    this.annotationService.deselectSegmentation();
  }

  dispose(): void {
    this.annotationService.deselectSegmentation();
    this.annotationsStore.setSegmentations([]);
    this.annotationsStore.setImageName('', '', 'None');
    this.annotationsStore.image = undefined;
    this.annotationTypeBl.freeAccessFinished();
    super.dispose();
  }
}
